import React, { useState, useCallback, useMemo, useEffect } from 'react';

interface JsonEditorProps {
  content: string;
  fileName: string;
  onSave?: (content: string) => void;
  isLoading?: boolean;
  readonly?: boolean;
}

export const JsonEditor: React.FC<JsonEditorProps> = ({
  content,
  fileName,
  onSave,
  isLoading = false,
  readonly = false
}) => {
  const [editedContent, setEditedContent] = useState(content);
  // Sync local editor content when parent content prop changes
  useEffect(() => {
    setEditedContent(content);
    setHasChanges(false);
  }, [content]);
  const [hasChanges, setHasChanges] = useState(false);
  const [viewMode, setViewMode] = useState<'formatted' | 'raw'>('formatted');
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Parse and format JSON for display
  const parsedJson = useMemo(() => {
    try {
      setJsonError(null);
      const parsed = JSON.parse(editedContent || '{}');
      return {
        isValid: true,
        formatted: JSON.stringify(parsed, null, 2),
        parsed
      };
    } catch (error) {
      setJsonError(error instanceof Error ? error.message : 'Invalid JSON');
      return {
        isValid: false,
        formatted: editedContent,
        parsed: null
      };
    }
  }, [editedContent]);

  const handleContentChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newContent = e.target.value;
    setEditedContent(newContent);
    setHasChanges(newContent !== content);
  }, [content]);

  const handleSave = useCallback(() => {
    if (onSave && hasChanges && parsedJson.isValid) {
      onSave(editedContent);
      setHasChanges(false);
    }
  }, [onSave, editedContent, hasChanges, parsedJson.isValid]);

  const handleFormat = useCallback(() => {
    if (parsedJson.isValid) {
      setEditedContent(parsedJson.formatted);
      setHasChanges(parsedJson.formatted !== content);
    }
  }, [parsedJson, content]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.ctrlKey && e.key === 's') {
      e.preventDefault();
      handleSave();
    }
  }, [handleSave]);

  const renderJsonTree = (obj: any, depth = 0): React.ReactNode => {
    if (obj === null) return <span className="text-gray-500">null</span>;
    if (typeof obj === 'boolean') return <span className="text-blue-600">{obj.toString()}</span>;
    if (typeof obj === 'number') return <span className="text-green-600">{obj}</span>;
    if (typeof obj === 'string') return <span className="text-red-600">"{obj}"</span>;
    
    if (Array.isArray(obj)) {
      return (
        <div className={`${depth > 0 ? 'ml-4' : ''}`}>
          <span className="text-gray-700">[</span>
          {obj.map((item, index) => (
            <div key={index} className="ml-4">
              {renderJsonTree(item, depth + 1)}
              {index < obj.length - 1 && <span className="text-gray-700">,</span>}
            </div>
          ))}
          <span className="text-gray-700">]</span>
        </div>
      );
    }
    
    if (typeof obj === 'object') {
      const entries = Object.entries(obj);
      return (
        <div className={`${depth > 0 ? 'ml-4' : ''}`}>
          <span className="text-gray-700">{'{'}</span>
          {entries.map(([key, value], index) => (
            <div key={key} className="ml-4">
              <span className="text-purple-600">"{key}"</span>
              <span className="text-gray-700">: </span>
              {renderJsonTree(value, depth + 1)}
              {index < entries.length - 1 && <span className="text-gray-700">,</span>}
            </div>
          ))}
          <span className="text-gray-700">{'}'}</span>
        </div>
      );
    }
    
    return <span>{String(obj)}</span>;
  };

  return (
    <div className="json-editor h-full flex flex-col">
      <div className="flex items-center justify-between p-4 border-b bg-gray-50">
        <div className="flex items-center gap-2">
          <span className="text-lg">📋</span>
          <h3 className="font-medium text-gray-900">{fileName}</h3>
          {hasChanges && (
            <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
              Modified
            </span>
          )}
          {jsonError && (
            <span className="text-xs bg-red-100 text-red-800 px-2 py-1 rounded">
              Invalid JSON
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          <div className="flex border rounded overflow-hidden">
            <button
              onClick={() => setViewMode('formatted')}
              className={`px-2 py-1 text-xs ${
                viewMode === 'formatted' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Tree
            </button>
            <button
              onClick={() => setViewMode('raw')}
              className={`px-2 py-1 text-xs ${
                viewMode === 'raw' 
                  ? 'bg-blue-500 text-white' 
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              }`}
            >
              Raw
            </button>
          </div>
          
          {!readonly && (
            <>
              <button
                onClick={handleFormat}
                disabled={!parsedJson.isValid || isLoading}
                className="px-2 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
              >
                Format
              </button>
              
              {onSave && (
                <button
                  onClick={handleSave}
                  disabled={!hasChanges || !parsedJson.isValid || isLoading}
                  className={`px-3 py-1 text-sm rounded ${
                    hasChanges && parsedJson.isValid && !isLoading
                      ? 'bg-blue-500 text-white hover:bg-blue-600'
                      : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  }`}
                >
                  {isLoading ? 'Saving...' : 'Save'}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {jsonError && (
        <div className="px-4 py-2 bg-red-50 border-b text-sm text-red-700">
          <strong>JSON Error:</strong> {jsonError}
        </div>
      )}

      <div className="flex-1 overflow-hidden">
        {viewMode === 'formatted' && parsedJson.isValid ? (
          <div className="h-full overflow-auto p-4 bg-gray-50">
            <div className="font-mono text-sm">
              {renderJsonTree(parsedJson.parsed)}
            </div>
          </div>
        ) : (
          <div className="h-full p-4">
            <textarea
              value={editedContent}
              onChange={handleContentChange}
              onKeyDown={handleKeyDown}
              readOnly={readonly}
              className={`w-full h-full resize-none border rounded font-mono text-sm ${
                readonly 
                  ? 'bg-gray-50 text-gray-700 cursor-not-allowed' 
                  : 'bg-white text-gray-900 focus:border-blue-500 focus:ring-1 focus:ring-blue-500'
              } ${jsonError ? 'border-red-300' : ''}`}
              style={{ 
                minHeight: '400px',
                lineHeight: '1.5'
              }}
              placeholder={readonly ? 'JSON content...' : 'Enter JSON...'}
            />
          </div>
        )}
      </div>

      {!readonly && (
        <div className="p-4 border-t bg-gray-50 text-xs text-gray-500">
          <div className="flex justify-between">
            <span>Valid JSON: {parsedJson.isValid ? '✅' : '❌'}</span>
            <span>Size: {editedContent.length} chars</span>
          </div>
        </div>
      )}
    </div>
  );
};